import queryString from 'query-string/index.js';
import { LOGOUT, SHOP_ITEM_FOR_DEPOSIT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { showNotification } from '../notifications/ducks/notifications.js';
import { requestApi } from '../../services/api.js';
import {
	FREESPIN,
	DEPOSIT_BONUS,
	FREE_MONEY_BONUS,
	CASH_BONUS,
	FREEBET,
	LOTTERY,
	GOLDEN_CHIP,
	OPT_IN,
	CAMPAIGN_ACCESS
} from '../../constants/index.js';
import { paymentMethodsHelper } from './marketplace-utils';
import {
	accountHydrate,
	selectCurrency,
	selectRemainingGermanyBonusValue,
	SET_NUM_OF_ACTIVE_OFFERS,
	updateGermanyRemainingBonusValue
} from '~/components/account/ducks/index.js';
import { selectIsGermanRegulatedFranchise } from '~/components/brand/ducks';
import getFormattedBonus from '~/components/payment/deposit/data/formatBonusValue';
import imageFormatHandler from '../utilities/image-format';
import { redeemDepositFreebet, selectDepositFreebet, selectDepositOptIn } from '~/components/bonus/ducks/index';

const REDUCER_NAME = 'marketplace';

/**
 * Here we store states related to shared components between shop & promotions.
 * We also handle the states for the DetailView that displays detailed information
 * about the selected promotion/shop-item in the Sidebar.
 *
 * Logic for claiming an item in the shop & promotions page are found here.
 */

// ACTIONS
export const MARKETPLACE_SELECT_ITEM = '@komigen/marketplace/MARKETPLACE_SELECT_ITEM';
export const MARKETPLACE_FETCH_ITEM_LOADING = '@komigen/marketplace/MARKETPLACE_FETCH_ITEM_LOADING';
export const MARKETPLACE_FETCH_ITEM_LOADING_SUCCESS = '@komigen/marketplace/MARKETPLACE_FETCH_ITEM_LOADING_SUCCESS';
export const MARKETPLACE_FETCH_ITEM_LOADING_FAILURE = '@komigen/marketplace/MARKETPLACE_FETCH_ITEM_LOADING_FAILURE';
export const CLOSE_DETAIL_VIEW = '@komigen/marketplace/CLOSE_DETAIL_VIEW';
export const MARKETPLACE_TRANSLATIONS_UPDATE = '@komigen/marketplace/marketplace_translations_update';

// These two are imported in Shop/Promotions to remove a claimed item in respective item-arrays
export const MARKETPLACE_REDEEMED_SHOP_ITEM = '@komigen/marketplace/MARKETPLACE_REDEEMED_SHOP_ITEM';
export const MARKETPLACE_REDEEMED_PROMOTION_ITEM = '@komigen/marketplace/MARKETPLACE_REDEEMED_PROMOTION_ITEM';

export const MARKETPLACE_REDEEM_ITEM_DETAILS_CLOSE = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_DETAILS_CLOSE';
export const MARKETPLACE_REDEEM_ITEM_DETAILS_OPEN = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_DETAILS_OPEN';
export const MARKETPLACE_REDEEM_ITEM_DETAILS_SUCCESS = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_DETAILS_SUCCESS';
export const MARKETPLACE_REDEEM_ITEM_DETAILS_FAILURE = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_DETAILS_FAILURE';

// For receipts
export const MARKETPLACE_REDEEM_ITEM_SUCCESS_CLOSE = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_SUCCESS_CLOSE';
export const MARKETPLACE_REDEEM_ITEM_FAILURE_CLOSE = '@komigen/marketplace/MARKETPLACE_REDEEM_ITEM_FAILURE_CLOSE';

// For Deposit Bonuses
export const DEPOSIT_BONUS_ADD_ON_CONFIRM_OPEN = '@komigen/marketplace/DEPOSIT_BONUS_ADD_ON_CONFIRM_OPEN';
export const DEPOSIT_OK_SUCCESS = '@komigen/marketplace/DEPOSIT_OK_SUCCESS';
export const DEPOSIT_OK_START = '@komigen/marketplace/DEPOSIT_OK_START';
export const DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_CLOSE =
	'@komigen/MARKETPLACE/DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_CLOSE';
export const DEPOSIT_BONUS_ADD_ON_CONFIRM_CLOSE = '@komigen/MARKETPLACE/DEPOSIT_BONUS_ADD_ON_CONFIRM_CLOSE';
export const DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_OPEN =
	'@komigen/MARKETPLACE/DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_OPEN';

// For Lotteries
export const PURCHASE_LOTTERY_LOADING = '@komigen/marketplace/PURCHASE_LOTTERY_LOADING';

// Selectors
export const selectMarketplaceState = state => state[REDUCER_NAME];
export const selectCurrentItemDetails = state => state[REDUCER_NAME].currentItemDetails;
export const selectCurrentItemDetailsFail = state => state[REDUCER_NAME].hasCurrentItemDetailsFail;
export const selectMarketplaceItems = state => state[REDUCER_NAME].marketplaceItems;
export const selectMarketplaceLoading = state => state[REDUCER_NAME].isCurrentItemDetailsLoading;
export const selectRedeemItemSuccess = state => state[REDUCER_NAME].redeemItemSuccess;
export const selectRedeemItemFailure = state => state[REDUCER_NAME].redeemItemFailure;
export const selectRedeemItemDetails = state => state[REDUCER_NAME].redeemItemDetails;
export const selectDepositOkSuccess = state => state[REDUCER_NAME] && state[REDUCER_NAME].depositOkSuccess;
export const selectIsPurchaseLotteryLoading = state =>
	state[REDUCER_NAME] && state[REDUCER_NAME].isPurchaseLotteryLoading;

/**
 * will be imported by the test
 */

export const initialState = {
	currentItemDetails: null,
	hasCurrentItemDetailsFail: false,
	isCurrentItemDetailsLoading: false,
	isDetailViewClosed: false,
	isPurchaseLotteryLoading: false,
	redeemItemSuccess: null,
	redeemItemFailure: null,
	redeemItemDetails: {}
};

const reducer = (state = initialState, { type, payload } = {}) => {
	switch (type) {
		case MARKETPLACE_SELECT_ITEM: {
			return {
				...state,
				isCurrentItemDetailsLoading: false,
				currentItemDetails: payload
			};
		}
		case MARKETPLACE_FETCH_ITEM_LOADING_SUCCESS: {
			return {
				...state,
				hasCurrentItemDetailsFail: false,
				isCurrentItemDetailsLoading: false
			};
		}
		case MARKETPLACE_FETCH_ITEM_LOADING_FAILURE: {
			return {
				...state,
				hasCurrentItemDetailsFail: true,
				isCurrentItemDetailsLoading: false
			};
		}
		case MARKETPLACE_FETCH_ITEM_LOADING: {
			return {
				...state,
				hasCurrentItemDetailsFail: false,
				isCurrentItemDetailsLoading: true,
				redeemItemSuccess: null,
				redeemItemFailure: null
			};
		}
		case CLOSE_DETAIL_VIEW: {
			return {
				...state,
				currentItemDetails: null,
				isDetailViewClosed: true
			};
		}
		case MARKETPLACE_TRANSLATIONS_UPDATE: {
			return {
				...state,
				translations: {
					...payload
				}
			};
		}
		case MARKETPLACE_REDEEM_ITEM_DETAILS_OPEN: {
			return {
				...state,
				redeemItemDetails: {
					open: true
				}
			};
		}
		case MARKETPLACE_REDEEM_ITEM_SUCCESS_CLOSE: {
			return {
				...state,
				redeemItemSuccess: null
			};
		}
		case MARKETPLACE_REDEEM_ITEM_FAILURE_CLOSE: {
			return {
				...state,
				redeemItemFailure: null
			};
		}
		case MARKETPLACE_REDEEM_ITEM_DETAILS_CLOSE: {
			return {
				...state,
				redeemItemDetails: {
					...state.redeemItemDetails,
					open: false
				}
			};
		}
		case MARKETPLACE_REDEEM_ITEM_DETAILS_SUCCESS: {
			return {
				...state,
				redeemItemSuccess: payload
			};
		}
		case MARKETPLACE_REDEEM_ITEM_DETAILS_FAILURE: {
			return {
				...state,
				redeemItemFailure: payload
			};
		}
		case DEPOSIT_OK_START: {
			return {
				...state,
				depositOkSuccess: payload
			};
		}
		case DEPOSIT_OK_SUCCESS: {
			return {
				...state,
				depositOkSuccess: payload
			};
		}
		case DEPOSIT_BONUS_ADD_ON_CONFIRM_OPEN: {
			return {
				...state,
				redeemItemDetails: {
					...state.redeemItemDetails,
					addOnConfirmation: true
				}
			};
		}
		case DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_CLOSE: {
			return {
				...state,
				redeemItemDetails: {
					...state.redeemItemDetails,
					addOnConfirmOptions: false
				}
			};
		}
		case DEPOSIT_BONUS_ADD_ON_CONFIRM_CLOSE: {
			return {
				...state,
				redeemItemDetails: {
					...state.redeemItemDetails,
					addOnConfirmation: false
				}
			};
		}
		case DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_OPEN: {
			return {
				...state,
				redeemItemDetails: {
					...state.redeemItemDetails,
					addOnConfirmOptions: true
				}
			};
		}
		case PURCHASE_LOTTERY_LOADING: {
			return {
				...state,
				isPurchaseLotteryLoading: payload
			};
		}
		case LOGOUT: {
			return {};
		}
		default: {
			return state;
		}
	}
};

// Opening the receipts for a claimed item. "Success-screen" / "Failure-screen"
export const openRedeemItemSuccess = config => ({
	type: MARKETPLACE_REDEEM_ITEM_DETAILS_SUCCESS,
	payload: { ...config }
});
export const openRedeemItemFailure = config => ({
	type: MARKETPLACE_REDEEM_ITEM_DETAILS_FAILURE,
	payload: { ...config }
});
// Closing the receipts
export const closeRedeemItemSuccess = () => ({
	type: MARKETPLACE_REDEEM_ITEM_SUCCESS_CLOSE
});
export const closeRedeemItemFailure = () => ({
	type: MARKETPLACE_REDEEM_ITEM_FAILURE_CLOSE
});

// Clear bonus in payment reducer
export const clearPaymentSpec = () => ({
	type: SHOP_ITEM_FOR_DEPOSIT,
	payload: null
});

// Closing sidebar with  details
export const closeDetailView = () => ({
	type: CLOSE_DETAIL_VIEW
});
// Closes any ongoing claim of an item
export const marketplaceRedeemItemClose = () => ({
	type: MARKETPLACE_REDEEM_ITEM_DETAILS_CLOSE
});
// Update translations for marketplace
export const MarketplaceTranslationsUpdate = translations => ({
	type: MARKETPLACE_TRANSLATIONS_UPDATE,
	payload: translations
});

/*
	DepositBonuses may have addons (and those might have options) attached to it.
	These addons & options are displayed in modals, and those modals are triggered
	by toggling states with these actions.
*/
export const depositBonusAddOnConfirmOpen = () => ({
	type: DEPOSIT_BONUS_ADD_ON_CONFIRM_OPEN
});
export const depositBonusAddOnConfirmClose = () => ({
	type: DEPOSIT_BONUS_ADD_ON_CONFIRM_CLOSE
});
export const depositBonusAddOnConfirmOptionsOpen = () => ({
	type: DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_OPEN
});
export const depositBonusAddOnConfirmOptionsClose = () => ({
	type: DEPOSIT_BONUS_ADD_ON_CONFIRM_OPTIONS_CLOSE
});

export function fetchMarketplaceItem(id, type, subId = null) {
	return async (dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING });

		let URL = '';

		switch (type) {
			case GOLDEN_CHIP:
			case FREESPIN:
				URL = `/freespin/${id}?gameId=${subId}`;
				break;
			case FREEBET:
				URL = `/freebet/${id}`;
				break;
			case LOTTERY:
				URL = `/rest/lottery/${id}`;
				break;
			case OPT_IN:
				URL = `/rest/deposit-opt-in/${id}`;
				break;
			case CAMPAIGN_ACCESS:
				URL = `/rest/shop/items/campaign-access/${id}`;
				break;
			// Default endpoint works for DepositBonus, FreeMoneyBonus, CashBonus and Promotions.
			default:
				URL = `/rest/bonus/${id}`;
				break;
		}

		try {
			const { status, result, description } = await requestApi(URL);
			if (!status === 'SUCCESS') {
				dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING_FAILURE });
				dispatch(
					showNotification({
						message: description,
						color: 'red',
						autoHide: true,
						openInSidebar: true
					})
				);
				return;
			}
			if (status === 'SUCCESS') {
				const formattedResult = isPictureElementEnabled
					? imageFormatHandler({ imageDescriptors, imageFormats })([result])[0]
					: result;
				const remainingBonusValue = selectRemainingGermanyBonusValue(getState());
				const userCurrency = selectCurrency(getState());
				const germanRegulatedFranchise = selectIsGermanRegulatedFranchise(getState());
				const formattedBonus = getFormattedBonus(
					germanRegulatedFranchise,
					remainingBonusValue,
					formattedResult,
					userCurrency
				);

				dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING_SUCCESS });
				dispatch({
					type: MARKETPLACE_SELECT_ITEM,
					payload: formattedBonus
				});
				return;
			}
			dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING_FAILURE });
			dispatch(
				showNotification({
					message: description,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			return dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING_FAILURE });
		}
	};
}

export const isDepositOk = (item, selectedAddOn = {}) => {
	return async dispatch => {
		const { addOnId, addOnOptionId } = selectedAddOn;
		dispatch(marketplaceRedeemItemClose());
		const bonusPaymentMethods = paymentMethodsHelper(item);
		const internalBonusId = item.id;
		dispatch({ type: DEPOSIT_OK_START, payload: '' });
		const response = await requestApi('/rest/bonus/isDepositBonusOK');
		if (response.status === 'SUCCESS') {
			const queryObj = {
				...(internalBonusId ? { bonusId: internalBonusId } : {}),
				...(addOnId ? { bonusAddOnId: addOnId } : {}),
				...(addOnOptionId ? { addOnOptionId } : {}),
				...(bonusPaymentMethods ? { bonusPaymentMethods } : {}),
				...(item.type ? { type: item.type } : {})
			};
			const query = queryString.stringify(queryObj);
			dispatch({ type: DEPOSIT_OK_SUCCESS, payload: query });
		} else {
			dispatch(openRedeemItemFailure(response));
		}
	};
};

export function redeemFMBWithPoints(item) {
	return async (dispatch, getState) => {
		dispatch(marketplaceRedeemItemClose());
		const internalBonusId = item.id;
		const response = await requestApi(
			'/rest/bonus/redeemFreeAndCashBonusesWithPlayerPoints',
			{ value: internalBonusId },
			'POST'
		);

		if (response.status === 'SUCCESS') {
			const response2 = await requestApi('/player/points');
			if (response2.status === 'SUCCESS') {
				dispatch({
					type: MARKETPLACE_REDEEMED_SHOP_ITEM,
					payload: item.id
				});
				dispatch(
					openRedeemItemSuccess({
						balanceAmount: response.result.balance,
						points: response2.result.amount
					})
				);
				if (selectIsGermanRegulatedFranchise(getState())) {
					dispatch(updateGermanyRemainingBonusValue());
				}
			} else {
				dispatch({
					type: MARKETPLACE_REDEEMED_SHOP_ITEM,
					payload: item.id
				});
			}
		} else {
			return dispatch(openRedeemItemFailure(response));
		}
		dispatch(accountHydrate());
	};
}

export function redeemFMB(item) {
	return async (dispatch, getState) => {
		const internalBonusId = item.id;
		dispatch(marketplaceRedeemItemClose());
		const response = await requestApi('/rest/bonus/redeemfreemoneybonus', { value: internalBonusId }, 'POST');
		if (response.status === 'SUCCESS') {
			const result = JSON.parse(response.result);
			dispatch({
				type: MARKETPLACE_REDEEMED_PROMOTION_ITEM,
				payload: item.id
			});
			dispatch(
				openRedeemItemSuccess({
					balanceAmount: result.balance
				})
			);
			if (selectIsGermanRegulatedFranchise(getState())) {
				dispatch(updateGermanyRemainingBonusValue());
			}
		} else {
			dispatch(openRedeemItemFailure(response));
		}
	};
}

export const redeemCampaignAccess = id => async dispatch => {
	dispatch(marketplaceRedeemItemClose());
	const response = await requestApi(`/rest/shop/activate/campaign-access/${id}`, {}, 'POST');

	if (response.status === 'SUCCESS') {
		const pointsResponse = await requestApi('/player/points');
		if (pointsResponse.status === 'SUCCESS') {
			dispatch({
				type: MARKETPLACE_REDEEMED_SHOP_ITEM,
				payload: id
			});
			dispatch(
				openRedeemItemSuccess({
					points: pointsResponse.result.amount
				})
			);
		} else {
			dispatch({
				type: MARKETPLACE_REDEEMED_SHOP_ITEM,
				payload: id
			});
		}
	} else {
		return dispatch(openRedeemItemFailure(response));
	}
	dispatch(accountHydrate());
};

export function redeemDepositBonus(item) {
	return async dispatch => {
		const { bonusAddOn } = item;
		dispatch(marketplaceRedeemItemClose());
		if (bonusAddOn) {
			dispatch(depositBonusAddOnConfirmOpen());
		} else {
			dispatch(isDepositOk(item));
		}
	};
}

export function redeemFreebetAndFreespin(item) {
	return async dispatch => {
		const internalBonusId = item.id;
		const type = item.type === GOLDEN_CHIP ? 'freespin' : item.type.toLowerCase();
		const URL = `/${type}/activate/${internalBonusId}`;
		dispatch(marketplaceRedeemItemClose());
		const response = await requestApi(URL, {}, 'POST');
		if (response.status === 'SUCCESS') {
			const response2 = await requestApi('/player/points');
			if (response2.status === 'SUCCESS') {
				dispatch({
					type: MARKETPLACE_REDEEMED_SHOP_ITEM,
					payload: item.id
				});
				dispatch(
					openRedeemItemSuccess({
						points: response2.result.amount
					})
				);
			} else {
				dispatch({
					type: MARKETPLACE_REDEEMED_SHOP_ITEM,
					payload: item.id
				});
			}
		} else {
			return dispatch(openRedeemItemFailure(response));
		}
		dispatch(accountHydrate());
	};
}

export function redeemLotteryTicket(item, amountOfTickets) {
	return async (dispatch, getState) => {
		dispatch({ type: PURCHASE_LOTTERY_LOADING, payload: true });
		const lotteryId = item.id;
		const response = await requestApi(
			'/rest/lottery/buyTickets',
			{
				lotteryId,
				amountOfTickets
			},
			'POST'
		);
		if (response.status === 'SUCCESS') {
			const secondResponse = await requestApi('/player/points');
			if (secondResponse.status === 'SUCCESS') {
				dispatch({ type: PURCHASE_LOTTERY_LOADING, payload: false });
				const { numOfActiveOffers } = getState().account;
				dispatch({
					type: SET_NUM_OF_ACTIVE_OFFERS,
					payload: (numOfActiveOffers || 0) + 1
				});
				return dispatch(
					openRedeemItemSuccess({
						points: secondResponse.result.amount
					})
				);
			}
			dispatch({ type: PURCHASE_LOTTERY_LOADING, payload: false });
			dispatch(openRedeemItemFailure(response));
		}
		return dispatch(openRedeemItemFailure(response));
	};
}

export const redeemMarketplaceItem =
	(item, options = {}) =>
	async dispatch => {
		const itemType = item.bonusType || item.type;
		switch (itemType) {
			case FREE_MONEY_BONUS:
			case CASH_BONUS: {
				if (options.itemCost > 0) {
					dispatch(redeemFMBWithPoints(item));
				} else {
					dispatch(redeemFMB(item));
				}
				break;
			}
			case DEPOSIT_BONUS: {
				dispatch({
					type: SHOP_ITEM_FOR_DEPOSIT,
					payload: {
						bonusId: item.id
					}
				});
				dispatch(redeemDepositBonus(item));
				break;
			}
			case GOLDEN_CHIP:
			case FREESPIN:
			case FREEBET: {
				if (item.freebetWithoutConditions) {
					dispatch(redeemDepositFreebet(item.id));

					dispatch({
						type: MARKETPLACE_REDEEMED_PROMOTION_ITEM,
						payload: item.id
					});
					dispatch(openRedeemItemSuccess());
				} else if (item.deposit) {
					dispatch(selectDepositFreebet(item));
					dispatch({
						type: SHOP_ITEM_FOR_DEPOSIT,
						payload: {
							bonusId: null,
							type: itemType
						}
					});
					dispatch(redeemDepositBonus(item));
				} else {
					dispatch(redeemFreebetAndFreespin(item));
				}

				break;
			}
			case OPT_IN: {
				dispatch(selectDepositOptIn(item));
				dispatch({
					type: SHOP_ITEM_FOR_DEPOSIT,
					payload: {
						bonusId: null,
						type: itemType
					}
				});
				dispatch(redeemDepositBonus(item));
				break;
			}
			case CAMPAIGN_ACCESS: {
				dispatch(redeemCampaignAccess(item.id));
				break;
			}
			case LOTTERY: {
				dispatch(redeemLotteryTicket(item, options.amountOfTickets));
				break;
			}
			default: {
				throw new Error(`Unhandled item: ${itemType}`);
			}
		}
	};

export const showLotteryResults = (id, drawDate) => {
	return async dispatch => {
		dispatch({ type: MARKETPLACE_FETCH_ITEM_LOADING });
		const response = await requestApi(
			`/rest/lottery/lotteryResult`,
			{
				lotteryId: id,
				time: drawDate
			},
			'POST'
		);

		if (response.status === 'SUCCESS') {
			dispatch({
				type: MARKETPLACE_FETCH_ITEM_LOADING_SUCCESS
			});
			dispatch({
				type: MARKETPLACE_SELECT_ITEM,
				payload: response.result
			});
		} else {
			dispatch({
				type: MARKETPLACE_FETCH_ITEM_LOADING_FAILURE
			});
			dispatch(
				showNotification({
					message: response.description,
					color: 'red',
					autoHide: true,
					openInSidebar: true
				})
			);
		}
	};
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
